import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading2,
  RemoveFormatting,
  Undo2,
  Redo2,
  Indent,
  Outdent,
  Table2,
  Minus,
  Subscript,
  Superscript,
} from "lucide-react";
import { plainToLetterHtml } from "@/lib/letterHtml";

const FONTS = [
  { label: "Default", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Calibri", value: "Calibri, Candara, Segoe UI, sans-serif" },
  { label: "Segoe UI", value: "\"Segoe UI\", Tahoma, Geneva, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "\"Trebuchet MS\", Helvetica, sans-serif" },
  { label: "Century Gothic", value: "\"Century Gothic\", CenturyGothic, sans-serif" },
  { label: "Times New Roman", value: "\"Times New Roman\", Times, serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Garamond", value: "Garamond, Baskerville, \"Times New Roman\", serif" },
  { label: "Palatino", value: "\"Palatino Linotype\", Palatino, serif" },
  { label: "Cambria", value: "Cambria, Georgia, serif" },
  { label: "Courier New", value: "\"Courier New\", Courier, monospace" },
  { label: "Consolas", value: "Consolas, \"Courier New\", monospace" },
];

const SIZES = [
  { label: "Size", value: "" },
  { label: "9", value: "9pt" },
  { label: "10", value: "10pt" },
  { label: "11", value: "11pt" },
  { label: "12", value: "12pt" },
  { label: "14", value: "14pt" },
  { label: "16", value: "16pt" },
  { label: "18", value: "18pt" },
  { label: "20", value: "20pt" },
  { label: "24", value: "24pt" },
];

const COLORS = [
  "#0f172a",
  "#334155",
  "#226DB4",
  "#089949",
  "#b45309",
  "#be123c",
  "#6d28d9",
  "#000000",
];

function ToolbarBtn({ onClick, title, active, children, testid }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick?.();
      }}
      className={`p-1.5 rounded border text-slate-700 hover:bg-white ${
        active ? "bg-white border-[#226DB4] text-[#226DB4]" : "border-transparent"
      }`}
    >
      {children}
    </button>
  );
}

function selCls() {
  return "h-7 text-xs border border-slate-200 rounded px-1.5 bg-white text-slate-700 max-w-[120px]";
}

/**
 * Rich letter editor — Word-like toolbar (fonts, lists, tables, align, etc.).
 * HTML is stored for preview and PDF.
 */
const LetterRichEditor = forwardRef(function LetterRichEditor(
  {
    value = "",
    onChange,
    minHeight = 280,
    testid = "letter-rich-editor",
    placeholder = "Write the letter… Use placeholders like {{employee_name}}",
    baseStyle = null,
    compact = false,
  },
  ref
) {
  const edRef = useRef(null);
  const lastEmitted = useRef("");
  const [font, setFont] = useState("");
  const [size, setSize] = useState("");
  const [color, setColor] = useState("#0f172a");

  const syncFromValue = (raw) => {
    const html = plainToLetterHtml(raw || "");
    if (!edRef.current) return;
    if (edRef.current.innerHTML !== html) {
      edRef.current.innerHTML = html;
    }
    lastEmitted.current = html;
  };

  useEffect(() => {
    const next = plainToLetterHtml(value || "");
    if (next !== lastEmitted.current) {
      syncFromValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = () => {
    if (!edRef.current) return;
    const html = edRef.current.innerHTML;
    lastEmitted.current = html;
    onChange?.(html);
  };

  const run = (cmd, arg = null) => {
    edRef.current?.focus();
    try {
      document.execCommand("styleWithCSS", false, true);
    } catch (_) {
      /* ignore */
    }
    document.execCommand(cmd, false, arg);
    emit();
  };

  const applyInlineStyle = (css) => {
    edRef.current?.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      return false;
    }
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    Object.assign(span.style, css);
    try {
      range.surroundContents(span);
    } catch {
      const frag = range.extractContents();
      span.appendChild(frag);
      range.insertNode(span);
    }
    sel.removeAllRanges();
    const after = document.createRange();
    after.selectNodeContents(span);
    after.collapse(false);
    sel.addRange(after);
    emit();
    return true;
  };

  const onFontChange = (v) => {
    setFont(v);
    if (!v) return;
    if (!applyInlineStyle({ fontFamily: v })) {
      run("fontName", v.split(",")[0].replace(/"/g, "").trim());
    }
  };

  const onSizeChange = (v) => {
    setSize(v);
    if (!v) return;
    if (!applyInlineStyle({ fontSize: v })) {
      const pt = parseInt(v, 10) || 12;
      const map = pt <= 10 ? "2" : pt <= 12 ? "3" : pt <= 14 ? "4" : pt <= 18 ? "5" : pt <= 24 ? "6" : "7";
      run("fontSize", map);
    }
  };

  const onColorChange = (v) => {
    setColor(v);
    if (!applyInlineStyle({ color: v })) {
      run("foreColor", v);
    }
  };

  const insertTable = () => {
    edRef.current?.focus();
    const html = `
<table style="border-collapse:collapse;width:100%;margin:0.5em 0">
  <tr>
    <th style="border:1px solid #94a3b8;padding:6px;background:#f1f5f9;text-align:left">Header 1</th>
    <th style="border:1px solid #94a3b8;padding:6px;background:#f1f5f9;text-align:left">Header 2</th>
    <th style="border:1px solid #94a3b8;padding:6px;background:#f1f5f9;text-align:left">Header 3</th>
  </tr>
  <tr>
    <td style="border:1px solid #94a3b8;padding:6px">&nbsp;</td>
    <td style="border:1px solid #94a3b8;padding:6px">&nbsp;</td>
    <td style="border:1px solid #94a3b8;padding:6px">&nbsp;</td>
  </tr>
  <tr>
    <td style="border:1px solid #94a3b8;padding:6px">&nbsp;</td>
    <td style="border:1px solid #94a3b8;padding:6px">&nbsp;</td>
    <td style="border:1px solid #94a3b8;padding:6px">&nbsp;</td>
  </tr>
</table>
<p><br></p>`;
    document.execCommand("insertHTML", false, html);
    emit();
  };

  const insertHr = () => {
    edRef.current?.focus();
    document.execCommand("insertHTML", false, "<hr/><p><br></p>");
    emit();
  };

  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      edRef.current?.focus();
      const ok = document.execCommand("insertText", false, text);
      if (!ok) {
        document.execCommand(
          "insertHTML",
          false,
          text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        );
      }
      emit();
    },
    focus: () => edRef.current?.focus(),
    getHtml: () => edRef.current?.innerHTML || "",
  }));

  return (
    <div className="border border-slate-200 rounded overflow-hidden bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <select
          className={selCls()}
          value={font}
          title="Font"
          data-testid={`${testid}-font`}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => onFontChange(e.target.value)}
        >
          {FONTS.map((f) => (
            <option key={f.label} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          className={selCls()}
          value={size}
          title="Font size"
          data-testid={`${testid}-size`}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => onSizeChange(e.target.value)}
        >
          {SIZES.map((s) => (
            <option key={s.label} value={s.value}>{s.label}</option>
          ))}
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-slate-600 px-1" title="Text color">
          {!compact && <span className="hidden sm:inline">Color</span>}
          <input
            type="color"
            value={color}
            data-testid={`${testid}-color`}
            onMouseDown={(e) => e.preventDefault()}
            onChange={(e) => onColorChange(e.target.value)}
            className="h-7 w-8 p-0 border border-slate-200 rounded cursor-pointer bg-white"
          />
        </label>
        {!compact && (
          <div className="hidden sm:flex items-center gap-0.5">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                title={c}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onColorChange(c);
                }}
                className="h-4 w-4 rounded-sm border border-slate-200"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        )}
        <span className="w-px h-5 bg-slate-200 mx-0.5" />
        <ToolbarBtn title="Bold" onClick={() => run("bold")} testid={`${testid}-bold`}>
          <Bold className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Italic" onClick={() => run("italic")} testid={`${testid}-italic`}>
          <Italic className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Underline" onClick={() => run("underline")} testid={`${testid}-underline`}>
          <Underline className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Strikethrough" onClick={() => run("strikeThrough")} testid={`${testid}-strike`}>
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Subscript" onClick={() => run("subscript")} testid={`${testid}-sub`}>
          <Subscript className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Superscript" onClick={() => run("superscript")} testid={`${testid}-sup`}>
          <Superscript className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-slate-200 mx-0.5" />
        <ToolbarBtn title="Heading" onClick={() => run("formatBlock", "h2")} testid={`${testid}-h2`}>
          <Heading2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Bullet list" onClick={() => run("insertUnorderedList")} testid={`${testid}-ul`}>
          <List className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Numbered list" onClick={() => run("insertOrderedList")} testid={`${testid}-ol`}>
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Decrease indent" onClick={() => run("outdent")} testid={`${testid}-outdent`}>
          <Outdent className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Increase indent" onClick={() => run("indent")} testid={`${testid}-indent`}>
          <Indent className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-slate-200 mx-0.5" />
        <ToolbarBtn title="Align left" onClick={() => run("justifyLeft")} testid={`${testid}-left`}>
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Align center" onClick={() => run("justifyCenter")} testid={`${testid}-center`}>
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Align right" onClick={() => run("justifyRight")} testid={`${testid}-right`}>
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Justify" onClick={() => run("justifyFull")} testid={`${testid}-justify`}>
          <AlignJustify className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-slate-200 mx-0.5" />
        <ToolbarBtn title="Insert table" onClick={insertTable} testid={`${testid}-table`}>
          <Table2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Horizontal line" onClick={insertHr} testid={`${testid}-hr`}>
          <Minus className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-slate-200 mx-0.5" />
        <ToolbarBtn title="Undo" onClick={() => run("undo")} testid={`${testid}-undo`}>
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Redo" onClick={() => run("redo")} testid={`${testid}-redo`}>
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Clear formatting" onClick={() => run("removeFormat")} testid={`${testid}-clear`}>
          <RemoveFormatting className="h-3.5 w-3.5" />
        </ToolbarBtn>
      </div>
      <div
        ref={edRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        data-testid={`${testid}-body`}
        onInput={emit}
        onBlur={emit}
        className="letter-rich-body px-3 py-3 text-sm text-slate-800 leading-6 focus:outline-none overflow-y-auto"
        style={{ minHeight, ...(baseStyle || {}) }}
      />
      <style>{`
        .letter-rich-body:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
        .letter-rich-body p { margin: 0 0 0.75em; }
        .letter-rich-body h2 { font-size: 1.15rem; font-weight: 700; margin: 0 0 0.6em; }
        .letter-rich-body ul, .letter-rich-body ol { margin: 0 0 0.75em; padding-left: 1.4rem; }
        .letter-rich-body li { margin: 0.15em 0; }
        .letter-rich-body table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
        .letter-rich-body th, .letter-rich-body td { border: 1px solid #94a3b8; padding: 6px; }
        .letter-rich-body th { background: #f1f5f9; font-weight: 600; }
        .letter-rich-body table.letter-meta { margin: 0 0 0.75em; }
        .letter-rich-body table.letter-meta td { border: none; padding: 0; vertical-align: top; }
        .letter-rich-body table.letter-meta td:last-child { text-align: right; }
        .letter-rich-body hr { border: none; border-top: 1px solid #cbd5e1; margin: 0.75em 0; }
        .letter-rich-body blockquote {
          border-left: 3px solid #226DB4; margin: 0.5em 0; padding-left: 0.75em; color: #475569;
        }
      `}</style>
    </div>
  );
});

export default LetterRichEditor;
